import { addMinutes, format, parse } from 'date-fns';

interface AppointmentData {
  signerSelectsFromRange?: boolean;
  dssDate?: string;
  dssDateRangeEnd?: string;
  dssDateRangeStart?: string;
  dssStartTime?: string;
  dssEndTime?: string;
  isDssDateRange?: boolean;
}

interface AppointmentOption {
  appointmentDate: string;
  appointmentTime: string;
}

export const DEFAULT_DSS_TIME_OPTION = {
  startTime: '08:00:00',
  endTime: '15:00:00',
};

export const generateAppointmentOptions = (data: AppointmentData): AppointmentOption[] => {
  const {
    dssDate,
    dssDateRangeStart,
    dssDateRangeEnd,
    dssStartTime = DEFAULT_DSS_TIME_OPTION.startTime,
    dssEndTime = DEFAULT_DSS_TIME_OPTION.endTime,
    isDssDateRange,
  } = data;

  if (!dssDate && !dssDateRangeStart) {
    return [];
  }

  const appointmentOptions: AppointmentOption[] = [];

  // Helper to increment the time by 30 minutes
  const incrementTime = (time: string): string => {
    const [hours, minutes] = time.split(':').map(Number);
    const newMinutes = minutes === 0 ? 30 : 0;
    const newHours = minutes === 0 ? hours : hours + 1;
    return `${newHours.toString().padStart(2, '0')}:${newMinutes.toString().padStart(2, '0')}:00`;
  };

  // Helper to iterate over time range for a single day
  const addAppointmentsForDay = (date: string) => {
    let time = dssStartTime;
    const endTimeObj = parse(dssEndTime, 'HH:mm:ss', new Date());

    while (time < dssEndTime) {
      const appointmentEndTime = parse(time, 'HH:mm:ss', new Date());
      const appointmentEndPlusOneHour = addMinutes(appointmentEndTime, 60);

      // Add appointment only if it ends before or at dssEndTime
      if (appointmentEndPlusOneHour <= endTimeObj) {
        appointmentOptions.push({
          appointmentDate: date,
          appointmentTime: time,
        });
      }

      time = incrementTime(time);
    }
  };

  // Case 1: Single date (no range)
  if (!isDssDateRange && dssDate) {
    addAppointmentsForDay(dssDate);
  }

  // Case 2: Date range is provided
  if (isDssDateRange && dssDateRangeStart && dssDateRangeEnd) {
    const startDate = parse(dssDateRangeStart, 'yyyy-MM-dd', new Date());
    const endDate = parse(dssDateRangeEnd, 'yyyy-MM-dd', new Date());

    // Loop through each day in the range
    for (let currentDate = startDate; currentDate <= endDate; currentDate.setDate(currentDate.getDate() + 1)) {
      const formattedDate = format(currentDate, 'yyyy-MM-dd');
      addAppointmentsForDay(formattedDate);
    }
  }

  return appointmentOptions;
};
